import React, { createContext, useContext, useState } from 'react';

export type Lang = 'nl' | 'en';

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  nl: {
    nav: { dashboard: 'Dashboard', reports: 'Rapportage', onboarding: 'Onboarding', team: 'Team training', billing: 'Facturatie', settings: 'Instellingen', logout: 'Afmelden' },
    lang: { label: 'Taal', nl: 'Nederlands', en: 'English' },
    status: {
      green: 'Voltooid', red: 'Afgewezen', expired: 'Geen reactie',
      pending: 'In behandeling', out_of_credits: 'Geen verificaties',
      approved: 'Voltooid', denied: 'Afgewezen',
    },
    dashboard: {
      title: 'Dashboard', subtitle: 'Laatste 30 dagen',
      totalVerifications: 'Totaal verificaties', successPct: 'Geslaagd %',
      rejected: 'Afgewezen', credits: 'Verificaties resterend',
      verificationsPerDay: 'Verificaties per dag', statusDistribution: 'Status verdeling',
      recentVerifications: 'Recente verificaties',
      loadError: 'Kon dashboardgegevens niet laden',
      noVerifications: 'Geen recente verificaties gevonden.',
      verifications: 'Verificaties',
      cols: { name: 'Naam', company: 'Bedrijf', contact: 'Contact', status: 'Status', document: 'Document', date: 'Datum' },
      notSet: 'Niet ingesteld',
    },
    reports: {
      title: 'Rapportage', subtitle: 'Automatische verzending & handmatige download',
      autoSend: {
        title: 'Automatische verzending',
        description: 'Ontvang automatisch een periodiek PDF-rapport per e-mail met een samenvatting van alle verificaties.',
        email: 'E-mailadres', emailPlaceholder: 'naam@bedrijf.be', interval: 'Interval',
        intervals: { '7d': 'Elke 7 dagen', '14d': 'Elke 14 dagen', '30d': 'Elke maand' } as Record<string, string>,
        save: 'Opslaan', saved: '✓ Instellingen opgeslagen',
      },
      download: {
        title: 'Rapport downloaden',
        description: 'Selecteer een datumbereik en formaat. CSV is direct beschikbaar; PDF duurt ±30 seconden.',
        period: 'Periode',
        format: 'Formaat', formatPdf: 'PDF', formatCsv: 'CSV',
        generate: 'Genereer rapport',
        generating: 'Verificaties ophalen...', generatingDesc: 'Data wordt geladen en rapport aangemaakt',
        polling: 'PDF wordt gegenereerd...', pollingDesc: 'Dit duurt ±30 seconden. De download start automatisch.',
        complete: 'Rapport klaar!', completeDesc: 'De download is automatisch gestart.',
        downloadAgain: 'Opnieuw downloaden', newReport: 'Nieuw rapport',
        failed: 'Generatie mislukt', retry: 'Opnieuw proberen',
      },
      detail: {
        title: 'Detail rapport opvragen',
        description: 'Geef een verificatie-ID (scanRef) in en download het detailrapport van iDenfy voor die specifieke verificatie.',
        scanRefLabel: 'Verificatie ID',
        scanRefPlaceholder: 'bv. 350e2420-8850-11e9-baa5-309c231b1bac',
        fetchBtn: 'Vraag detail rapport op',
        fetching: 'Detail rapport ophalen...',
        complete: 'Rapport klaar! De download is gestart.',
        notFound: 'Verificatie niet gevonden of behoort niet tot dit account.',
        failed: 'Kon detail rapport niet ophalen.',
      },
    },
    onboarding: {
      title: 'Onboarding mails',
      subtitle: 'Genereer klant-mails in uw eigen huisstijl & download of kopieer ze',
      company: {
        title: 'Uw bedrijf',
        companyLabel: 'Bedrijfsnaam', companyPlaceholder: 'Uw Bedrijf BV',
        websiteLabel: 'Website', websitePlaceholder: 'https://www.uwbedrijf.be',
        emailLabel: 'Support e-mail', emailPlaceholder: 'support@uwbedrijf.be',
        phoneLabel: 'Telefoon', phoneOptional: '(optioneel)', phonePlaceholder: '+32 ...',
        logoLabel: 'Logo', logoHint: '(PNG of JPG)',
        logoDropMain: 'Klik om te uploaden', logoDropAux: 'of sleep hierheen',
        logoUploaded: 'Geüpload', logoRemove: 'Verwijder',
      },
      brand: {
        title: 'Uw huisstijl',
        primaryLabel: 'Primaire merkkleur',
        paletteAria: 'Voorgestelde kleuren',
        fontLabel: 'Lettertype',
        toneLabel: 'Tone of voice', toneFormal: 'U-vorm (formeel)', toneCasual: 'Je-vorm (casual)',
      },
      details: {
        title: 'Details',
        serviceLabel: 'Naam van de service', serviceHint: '(zoals klant het ziet)',
        servicePlaceholder: 'beveiligde identiteitscontrole',
        goliveLabel: 'Go-live datum', golivePlaceholder: 'maandag 16 juni 2026',
        priceToggle: 'Prijs vermelden in de mails',
        priceLabel: 'Prijs per verificatie', priceHint: '(verkoopprijs)',
        reset: 'Alles wissen & opnieuw beginnen',
        resetConfirm: 'Alle gegevens wissen en opnieuw beginnen?',
      },
      preview: {
        announceNum: '01 · Aankondiging', announceTitle: 'We voeren ID-verificatie in',
        howNum:      '02 · Uitleg',       howTitle:      'Zo werkt het — in 30 sec',
        goliveNum:   '03 · Go-live',      goliveTitle:   'Morgen wordt het actief',
        subjectLabel: 'Onderwerp:',
        copySubject: 'Onderwerp', copyHtml: 'Kopieer HTML', download: 'Download',
        iframeTitle: 'E-mail preview',
        help: 'Tip: kopieer de HTML en plak ze in uw e-mailtool (Outlook, Gmail, Brevo, Mailchimp…). Alles blijft lokaal in uw browser.',
      },
      toast: {
        copied: 'Gekopieerd!',
        htmlCopied: 'HTML gekopieerd!',
        subjectCopied: 'Onderwerp gekopieerd!',
        downloaded: 'Gedownload!',
        copyFailed: 'Kon niet kopiëren',
        notAnImage: 'Kies een afbeelding',
        logoTooBig: 'Logo te groot (max 1 MB)',
      },
      fileSlugs: { announce: '01-aankondiging', how: '02-zo-werkt-het', golive: '03-go-live' },
    },
    billing: {
      title: 'Facturatie', subtitle: 'Beheer uw abonnement en verificaties',
      loading: 'Laden...', loadError: 'Kon de facturatiegegevens niet laden. Probeer het opnieuw.',
      status: {
        trialing: 'Trial', active: 'Actief', past_due: 'Betaling achterstallig',
        canceled: 'Opgezegd', incomplete: 'Onvolledig',
      },
      currentPlan: {
        title: 'Uw abonnement',
        trialNote: 'U gebruikt momenteel de gratis trial met 10 verificaties.',
        creditsRemaining: 'Verificaties resterend',
        planCredits: 'Maandelijkse verificaties',
        autoTopup: 'Auto-aanvulling',
        autoTopupCredits: 'verificaties per aanvulling',
        manageBtn: 'Facturen & betaalgegevens beheren',
      },
      plans: {
        title: 'Kies uw pakket',
        subtitle: 'Pakketten worden maandelijks gefactureerd. Overschakelen is mogelijk en gaat in na de huidige betaalperiode.',
        currentBadge: 'Huidig pakket',
        subscribeBtn: 'Kiezen',
        switchBtn: 'Overschakelen',
        perMonth: '/maand',
        verificationsPerMonth: 'verificaties per maand',
      },
      modal: {
        title: 'Abonnement afsluiten',
        autoTopupTitle: 'Automatisch bijkopen',
        autoTopupDesc: 'Wanneer uw verificaties op zijn, worden er automatisch extra verificaties bijgekocht aan €4 per verificatie. U kiest nu het aantal per aanvulling.',
        qtyLabel: 'Verificaties per aanvulling',
        qtyMin: 'Minimum 10 verificaties',
        consentLabel: 'Ik ga akkoord met het automatisch bijkopen van verificaties aan €4/verificatie wanneer mijn saldo op is.',
        proceedBtn: 'Doorgaan naar betaling',
        cancelBtn: 'Annuleren',
        consentRequired: 'Bevestig uw akkoord met de auto-aanvulling om verder te gaan.',
      },
      policy: {
        bannerTitle: 'Privacyverklaring tekenen',
        bannerDesc: 'Als onderdeel van uw abonnement dient u de privacyverklaring te lezen en te ondertekenen. Dit duurt slechts een minuut.',
        viewBtn: 'Bekijk privacyverklaring',
        signBtn: 'Ik ga akkoord en onderteken',
        signed: '✓ Privacyverklaring ondertekend',
        signing: 'Bezig...',
      },
      portal: {
        title: 'Stripe Klantportaal',
        description: 'Bekijk en download facturen, wijzig betaalgegevens of uw bedrijfsgegevens.',
        openBtn: 'Openen',
        noAccount: 'Geen facturatieaccount gevonden.',
      },
      tabs: {
        subscription: 'Abonnement',
        company:      'Bedrijfsgegevens',
        invoices:     'Facturen',
        payment:      'Betaalmethode',
      },
      company: {
        title: 'Bedrijfsgegevens',
        subtitle: 'Deze gegevens worden gebruikt op uw facturen en doorgegeven aan Stripe.',
        companyName: 'Bedrijfsnaam', vatNumber: 'BTW-nummer',
        vatHint: 'Bijv. BE0123456789',
        address: 'Adres (straat + nummer)', city: 'Gemeente', zip: 'Postcode', country: 'Land',
        save: 'Opslaan', saved: '✓ Opgeslagen', saving: 'Opslaan...',
        saveError: 'Opslaan mislukt. Controleer uw gegevens.',
        loadError: 'Kon bedrijfsgegevens niet laden.',
        noSubscription: 'Bedrijfsgegevens worden opgeslagen na het afsluiten van een abonnement.',
      },
      invoices: {
        title: 'Facturen',
        subtitle: 'Overzicht van alle facturen. Klik op de downloadknop om een PDF te openen.',
        noInvoices: 'Nog geen facturen beschikbaar.',
        loadError: 'Kon facturen niet laden.',
        colDate: 'Datum', colNumber: 'Factuurnummer', colPeriod: 'Periode',
        colAmount: 'Bedrag', colStatus: 'Status', colActions: '',
        download: 'PDF', open: 'Bekijk',
        statusPaid: 'Betaald', statusOpen: 'Openstaand', statusVoid: 'Geannuleerd', statusDraft: 'Concept',
        noSubscription: 'Facturen zijn beschikbaar zodra u een abonnement heeft.',
      },
      payment: {
        title: 'Betaalmethode',
        subtitle: 'Beheer de kaart die gebruikt wordt voor uw maandelijkse abonnement.',
        current: 'Huidige kaart',
        noMethod: 'Geen betaalmethode ingesteld.',
        expires: 'Geldig tot',
        updateBtn: 'Kaart bijwerken',
        cancelBtn: 'Annuleren',
        saveBtn: 'Opslaan',
        saving: 'Verwerken...',
        saved: '✓ Betaalmethode bijgewerkt',
        saveError: 'Bijwerken mislukt. Probeer opnieuw.',
        loadError: 'Kon betaalmethode niet laden.',
        noSubscription: 'Betaalmethode is beschikbaar zodra u een abonnement heeft.',
        cardLabel: 'Kaartgegevens',
        cardHint: 'Kaartgegevens worden versleuteld verwerkt door Stripe.',
      },
    },
    team: {
      title: 'Team training',
      subtitle: 'Nodig je medewerkers uit voor de XyloTrust identiteitsverificatie-training en volg hun voortgang',
      invite: {
        title: 'Medewerkers uitnodigen',
        label: 'E-mailadressen',
        placeholder: 'jan@uwbedrijf.be\nsara@uwbedrijf.be\n...',
        hint: 'Eén adres per regel, of gescheiden door komma\'s. Elke medewerker krijgt een e-mail met een persoonlijke trainingslink.',
        cta: 'Verstuur uitnodigingen',
        sending: 'Versturen...',
        emptyError: 'Geef minstens één e-mailadres in',
        invalidError: 'Geen geldige e-mailadressen gevonden',
        loadError: 'Kon team-overzicht niet laden',
      },
      stats: { total: 'Uitgenodigd', passed: 'Geslaagd', pending: 'In afwachting' },
      table: {
        title: 'Voortgang van het team',
        empty: 'Nog geen medewerkers uitgenodigd. Voeg e-mailadressen toe om te starten.',
        colAgent: 'Medewerker', colStatus: 'Status', colInvited: 'Uitgenodigd', colCompleted: 'Voltooid op', colActions: '',
        badgePassed: 'Geslaagd', badgePending: 'In afwachting',
        copyLink: 'Kopieer link', remove: 'Verwijder',
        confirmRemove: 'Deze medewerker verwijderen uit het team?',
      },
      toast: {
        invited:    'Uitnodiging(en) verstuurd',
        invitedSome: '{added} verstuurd, {skipped} stonden al in de lijst',
        sendFailed: 'Records aangemaakt maar mailen mislukte (zie Sentry — SES sandbox?)',
        copied:     'Trainingslink gekopieerd',
        copyFailed: 'Kon link niet kopiëren',
        removed:    'Verwijderd',
      },
      modal: {
        title: 'Uitnodiging verstuurd ✉️',
        to: 'Aan:', subject: 'Onderwerp:',
        subjectValue: 'Je XyloTrust-training staat klaar',
        body: 'Hi, je bent uitgenodigd om de korte XyloTrust identiteitsverificatie-training te doorlopen. Klik op de knop hieronder om te starten — het duurt ongeveer 5 minuten.',
        cta: 'Start je training →',
        close: 'Klaar',
      },
    },
    settings: {
      title: 'Instellingen',
      subtitle: 'Configuratie van uw XyloTrust-account',
      smsCountries: {
        title: 'Welke landen worden standaard ondersteund?',
        intro: 'XyloTrust ondersteunt standaard SMS-verificatie naar **19 landen**. België is de standaardkeuze in de verificatie-picker; de overige 18 zijn één klik verder.',
        defaultBadge: 'Standaard',
        supportNote: 'Een land dat hier niet bij staat?',
        supportNoteRest: 'Wij activeren bijkomende landen op aanvraag — neem contact op via',
        supportNoteEnd: 'met de landcodes die u nodig heeft.',
      },
    },
    training: {
      header:    'XyloTrust Training',
      subheader: 'Identiteitsverificatie',
      steps:     ['Overzicht', 'Oefening 1', 'Oefening 2', 'Geslaagd'],
      loading:   'Laden...',
      notFound:  'Trainingslink niet gevonden of ingetrokken',
      welcome: {
        title:  'Welkom bij de XyloTrust-training 👋',
        lead:   'In een paar minuten leer je hoe je de identiteit van een aanvrager verifieert vóór je een gevoelig verzoek uitvoert. Daarna doorloop je twee korte oefeningen. Slaag je voor beide, dan ben je gecertificeerd.',
        howItWorks: 'Hoe XyloTrust werkt',
        steps: [
          { title: '1. Stuur een verificatie', body: 'Open het XyloTrust-paneel in het ticket, geef het geregistreerde GSM-nummer van de klant in en verstuur de SMS.' },
          { title: '2. De klant verifieert',  body: 'De klant opent de link, scant zijn identiteitsdocument en neemt een selfie. De XyloTrust identity verification service vergelijkt beide.' },
          { title: '3. Beoordeel het resultaat', body: 'Het resultaat verschijnt als privénotitie. <b>Kritieke controle:</b> komt de geverifieerde naam overeen met de accounteigenaar? Zo niet — keur af en escaleer.' },
        ],
        cta: 'Start oefening 1 →',
      },
      ui: {
        scenarioTag: (n: number) => `Oefening ${n} van 2`,
        evaluate: 'Beoordeel deze aanvraag',
        evaluateLead: 'Lees het ticket, verstuur een verificatie via het XyloTrust-paneel, en beslis op basis van het resultaat of je het verzoek goed- of afkeurt.',
        ticketCreatedBy: 'Gemaakt door Admin User',
        replyTabs: { reply: 'Antwoorden', note: 'Opmerking', forward: 'Doorsturen' },
        replyPlaceholder: 'Typ hier uw antwoord…',
        widgetTitle: 'Identiteitscontrole',
        widgetLabel: '1. GSM-nummer klant',
        widgetSendBtn: '2. Verstuur SMS',
        widgetDirectCheck: 'Direct identiteitscheck',
        contactHeader: 'Contactinfo…',
        contactEdit: 'Bewerken',
        contactOrg: 'Acme Corp',
        contactEmailLabel: 'E-mail',
        contactPhoneLabel: 'Mobiele telefoon',
        contactMore: 'Meer informatie weergeven',
        progressSent:    'Bericht verstuurd',
        progressWait:    'Controle door klant',
        progressWaitSub: 'Wachten op klant',
        progressDone:    'Verificatie voltooid',
        showResults:     'Toon resultaten',
        notePrefix:      'Admin User heeft een privénotitie toegevoegd · zonet',
        noteFailedTitle: 'identity verification failed',
        noteFailedBody:  'De XyloTrust identity verification service kon de verificatie niet valideren.',
        noteOkTitle:     'Identity Controles voltooid.',
        noteDoc:         'Document:',
        noteName:        'Naam:',
        noteBirth:       'Geboorteplaats:',
        noteCheck:       'Controle:',
        noteCountry:     'Land:',
        noteIP:          'IP Adres:',
        noteVid:         'Verificatie ID:',
        decideQ:         'Wat doe je met dit verzoek?',
        decideHint:      'Vergelijk de geverifieerde naam met de accounteigenaar (<b>{account}</b>) en beoordeel het resultaat.',
        approve:         'Goedkeuren & uitvoeren',
        reject:          'Afkeuren & escaleren',
        feedbackOk:      'Goed gedaan',
        feedbackBad:     'Niet juist',
        nextBtn:         'Volgende oefening →',
        finishBtn:       'Voltooi training →',
        retryBtn:        'Opnieuw proberen',
      },
      passed: {
        title:    'Je bent geslaagd! 🎓',
        body:     'Je hebt beide oefeningen correct afgerond en begrijpt wanneer je een verificatie goed- of afkeurt. Je certificaat is teruggekoppeld naar de portal — je beheerder ziet nu dat je de training voltooid hebt.',
        certName: 'XyloTrust Identity Verification',
        certMeta: 'voltooid op',
        already:  'Reeds voltooid op',
      },
      scenarios: [
        {
          subject:   'Forgot my password',
          via:       'gemeld via telefoon · 28 mei 2026 10:56',
          body: [
            'Hi Support,',
            'I forgot my password, can u help me out here?',
            'My email is bill.lates@acmecorp.com',
            'Thanks!',
          ],
          explainOk:  'Correct. De geverifieerde naam (BILL LATES) komt overeen met de accounteigenaar en de selfie matcht het document. Je mag het verzoek uitvoeren.',
          explainBad: 'Fout. Dit resultaat is geldig: de naam komt overeen met de accounteigenaar en de selfie matcht. Dit verzoek had je moeten goedkeuren.',
        },
        {
          subject:   'Need access to the finance dashboard urgently',
          via:       'gemeld via e-mail · 3 jun 2026 09:41',
          body: [
            'Hi,',
            'I urgently need my access restored to the finance dashboard, I have a board meeting in 5 minutes. Please just push it through.',
            'Regards',
          ],
          explainOk:  'Fout. De verificatie is mislukt ("identity verification failed"). Bij een mislukte verificatie mag je een gevoelig verzoek nooit uitvoeren — je had moeten afkeuren en escaleren.',
          explainBad: 'Correct. De verificatie is mislukt, dus de identiteit is niet bevestigd. Je keurt het verzoek af en escaleert volgens de SOP — zeker bij de ongewone urgentie hier (een klassiek social-engineering-signaal).',
        },
      ],
    },
    auth: { loginFailed: 'Aanmelden mislukt', retry: 'Opnieuw proberen', loggingIn: 'Aanmelden...' },
    uninstallBanner: {
      title: 'XyloTrust is gedeïnstalleerd',
      bodyDays: 'Je gegevens worden binnen {days} dagen verwijderd conform onze voorwaarden.',
      bodyToday: 'Je gegevens worden vandaag verwijderd conform onze voorwaarden.',
      cta: 'Installeer de app opnieuw om dit te voorkomen.',
      reinstallLink: 'Installatie-instructies',
    },
  },

  en: {
    nav: { dashboard: 'Dashboard', reports: 'Reporting', onboarding: 'Onboarding', team: 'Team training', billing: 'Billing', settings: 'Settings', logout: 'Sign out' },
    lang: { label: 'Language', nl: 'Dutch', en: 'English' },
    status: {
      green: 'Completed', red: 'Rejected', expired: 'No response',
      pending: 'Pending', out_of_credits: 'No verifications',
      approved: 'Completed', denied: 'Rejected',
    },
    dashboard: {
      title: 'Dashboard', subtitle: 'Last 30 days',
      totalVerifications: 'Total verifications', successPct: 'Success %',
      rejected: 'Rejected', credits: 'Verifications remaining',
      verificationsPerDay: 'Verifications per day', statusDistribution: 'Status distribution',
      recentVerifications: 'Recent verifications',
      loadError: 'Failed to load dashboard data',
      noVerifications: 'No recent verifications found.',
      verifications: 'Verifications',
      cols: { name: 'Name', company: 'Company', contact: 'Contact', status: 'Status', document: 'Document', date: 'Date' },
      notSet: 'Not set',
    },
    reports: {
      title: 'Reporting', subtitle: 'Automatic delivery & manual download',
      autoSend: {
        title: 'Automatic delivery',
        description: 'Automatically receive a periodic PDF report by email with a summary of all verifications.',
        email: 'Email address', emailPlaceholder: 'name@company.com', interval: 'Interval',
        intervals: { '7d': 'Every 7 days', '14d': 'Every 14 days', '30d': 'Monthly' } as Record<string, string>,
        save: 'Save', saved: '✓ Settings saved',
      },
      download: {
        title: 'Download report',
        description: 'Select a date range and format. CSV is available immediately; PDF takes ±30 seconds.',
        period: 'Period',
        format: 'Format', formatPdf: 'PDF', formatCsv: 'CSV',
        generate: 'Generate report',
        generating: 'Fetching verifications...', generatingDesc: 'Loading data and creating report',
        polling: 'Generating PDF...', pollingDesc: 'This takes ±30 seconds. The download will start automatically.',
        complete: 'Report ready!', completeDesc: 'The download has started automatically.',
        downloadAgain: 'Download again', newReport: 'New report',
        failed: 'Generation failed', retry: 'Try again',
      },
      detail: {
        title: 'Request detail report',
        description: 'Enter a verification ID (scanRef) and download the iDenfy detail report for that specific verification.',
        scanRefLabel: 'Verification ID',
        scanRefPlaceholder: 'e.g. 350e2420-8850-11e9-baa5-309c231b1bac',
        fetchBtn: 'Request detail report',
        fetching: 'Fetching detail report...',
        complete: 'Report ready! The download has started.',
        notFound: 'Verification not found or does not belong to this account.',
        failed: 'Could not fetch detail report.',
      },
    },
    onboarding: {
      title: 'Onboarding emails',
      subtitle: 'Generate customer emails in your own brand style & download or copy them',
      company: {
        title: 'Your company',
        companyLabel: 'Company name', companyPlaceholder: 'Your Company Ltd',
        websiteLabel: 'Website', websitePlaceholder: 'https://www.yourcompany.com',
        emailLabel: 'Support email', emailPlaceholder: 'support@yourcompany.com',
        phoneLabel: 'Phone', phoneOptional: '(optional)', phonePlaceholder: '+1 ...',
        logoLabel: 'Logo', logoHint: '(PNG or JPG)',
        logoDropMain: 'Click to upload', logoDropAux: 'or drag here',
        logoUploaded: 'Uploaded', logoRemove: 'Remove',
      },
      brand: {
        title: 'Your brand identity',
        primaryLabel: 'Primary brand colour',
        paletteAria: 'Suggested colours',
        fontLabel: 'Font',
        toneLabel: 'Tone of voice', toneFormal: 'Formal', toneCasual: 'Casual',
      },
      details: {
        title: 'Details',
        serviceLabel: 'Service name', serviceHint: '(as the customer sees it)',
        servicePlaceholder: 'secure identity verification',
        goliveLabel: 'Go-live date', golivePlaceholder: 'Monday 16 June 2026',
        priceToggle: 'Mention price in the emails',
        priceLabel: 'Price per verification', priceHint: '(sell price)',
        reset: 'Clear everything & start over',
        resetConfirm: 'Clear all data and start over?',
      },
      preview: {
        announceNum: '01 · Announcement', announceTitle: "We're introducing ID verification",
        howNum:      '02 · How it works', howTitle:      'How it works — in 30 sec',
        goliveNum:   '03 · Go-live',      goliveTitle:   'It goes live tomorrow',
        subjectLabel: 'Subject:',
        copySubject: 'Subject', copyHtml: 'Copy HTML', download: 'Download',
        iframeTitle: 'Email preview',
        help: 'Tip: copy the HTML and paste it into your email tool (Outlook, Gmail, Brevo, Mailchimp…). Everything stays local in your browser.',
      },
      toast: {
        copied: 'Copied!',
        htmlCopied: 'HTML copied!',
        subjectCopied: 'Subject copied!',
        downloaded: 'Downloaded!',
        copyFailed: 'Could not copy',
        notAnImage: 'Choose an image',
        logoTooBig: 'Logo too large (max 1 MB)',
      },
      fileSlugs: { announce: '01-announcement', how: '02-how-it-works', golive: '03-go-live' },
    },
    billing: {
      title: 'Billing', subtitle: 'Manage your subscription and verifications',
      loading: 'Loading...', loadError: 'Failed to load billing details. Please try again.',
      status: {
        trialing: 'Trial', active: 'Active', past_due: 'Payment overdue',
        canceled: 'Canceled', incomplete: 'Incomplete',
      },
      currentPlan: {
        title: 'Your subscription',
        trialNote: 'You are currently on the free trial with 10 verifications.',
        creditsRemaining: 'Verifications remaining',
        planCredits: 'Monthly verifications',
        autoTopup: 'Auto top-up',
        autoTopupCredits: 'verifications per top-up',
        manageBtn: 'Manage invoices & payment details',
      },
      plans: {
        title: 'Choose your plan',
        subtitle: 'Plans are billed monthly. You can switch plans at any time; changes take effect at the next billing cycle.',
        currentBadge: 'Current plan',
        subscribeBtn: 'Get started',
        switchBtn: 'Switch plan',
        perMonth: '/month',
        verificationsPerMonth: 'verifications per month',
      },
      modal: {
        title: 'Start subscription',
        autoTopupTitle: 'Automatic top-up',
        autoTopupDesc: 'When your verifications run out, additional verifications are automatically purchased at €4 per verification. Choose the quantity per top-up.',
        qtyLabel: 'Verifications per top-up',
        qtyMin: 'Minimum 10 verifications',
        consentLabel: 'I agree to the automatic purchase of verifications at €4/verification when my balance runs out.',
        proceedBtn: 'Proceed to payment',
        cancelBtn: 'Cancel',
        consentRequired: 'Please confirm your agreement to the auto top-up to proceed.',
      },
      policy: {
        bannerTitle: 'Sign the privacy policy',
        bannerDesc: 'As part of your subscription, you need to read and sign the privacy policy. It only takes a minute.',
        viewBtn: 'View privacy policy',
        signBtn: 'I agree and sign',
        signed: '✓ Privacy policy signed',
        signing: 'Processing...',
      },
      portal: {
        title: 'Stripe Customer Portal',
        description: 'View and download invoices, update payment details or company information.',
        openBtn: 'Open',
        noAccount: 'No billing account found.',
      },
      tabs: {
        subscription: 'Subscription',
        company:      'Company details',
        invoices:     'Invoices',
        payment:      'Payment method',
      },
      company: {
        title: 'Company details',
        subtitle: 'This information appears on your invoices and is passed to Stripe.',
        companyName: 'Company name', vatNumber: 'VAT number',
        vatHint: 'E.g. BE0123456789',
        address: 'Address (street + number)', city: 'City', zip: 'Postal code', country: 'Country',
        save: 'Save', saved: '✓ Saved', saving: 'Saving...',
        saveError: 'Failed to save. Please check your details.',
        loadError: 'Failed to load company details.',
        noSubscription: 'Company details can be saved once you have an active subscription.',
      },
      invoices: {
        title: 'Invoices',
        subtitle: 'All invoices for your account. Click the download button to open the PDF.',
        noInvoices: 'No invoices yet.',
        loadError: 'Failed to load invoices.',
        colDate: 'Date', colNumber: 'Invoice number', colPeriod: 'Period',
        colAmount: 'Amount', colStatus: 'Status', colActions: '',
        download: 'PDF', open: 'View',
        statusPaid: 'Paid', statusOpen: 'Open', statusVoid: 'Void', statusDraft: 'Draft',
        noSubscription: 'Invoices will be available once you have a subscription.',
      },
      payment: {
        title: 'Payment method',
        subtitle: 'Manage the card used for your monthly subscription.',
        current: 'Current card',
        noMethod: 'No payment method on file.',
        expires: 'Expires',
        updateBtn: 'Update card',
        cancelBtn: 'Cancel',
        saveBtn: 'Save',
        saving: 'Processing...',
        saved: '✓ Payment method updated',
        saveError: 'Update failed. Please try again.',
        loadError: 'Failed to load payment method.',
        noSubscription: 'Payment method is available once you have a subscription.',
        cardLabel: 'Card details',
        cardHint: 'Card details are encrypted and processed by Stripe.',
      },
    },
    team: {
      title: 'Team training',
      subtitle: 'Invite your agents to the XyloTrust identity-verification training and track their progress',
      invite: {
        title: 'Invite team members',
        label: 'Email addresses',
        placeholder: 'jan@yourcompany.com\nsara@yourcompany.com\n...',
        hint: 'One address per line, or comma-separated. Each member gets an email with their personal training link.',
        cta: 'Send invitations',
        sending: 'Sending...',
        emptyError: 'Enter at least one email address',
        invalidError: 'No valid email addresses found',
        loadError: 'Failed to load team overview',
      },
      stats: { total: 'Invited', passed: 'Passed', pending: 'Pending' },
      table: {
        title: 'Team progress',
        empty: 'No team members invited yet. Add email addresses to get started.',
        colAgent: 'Team member', colStatus: 'Status', colInvited: 'Invited', colCompleted: 'Completed on', colActions: '',
        badgePassed: 'Passed', badgePending: 'Pending',
        copyLink: 'Copy link', remove: 'Remove',
        confirmRemove: 'Remove this team member?',
      },
      toast: {
        invited:    'Invitation(s) sent',
        invitedSome: '{added} sent, {skipped} were already in the list',
        sendFailed: 'Records created but sending the mail failed (see Sentry — SES sandbox?)',
        copied:     'Training link copied',
        copyFailed: 'Could not copy link',
        removed:    'Removed',
      },
      modal: {
        title: 'Invitation sent ✉️',
        to: 'To:', subject: 'Subject:',
        subjectValue: 'Your XyloTrust training is ready',
        body: "Hi, you've been invited to complete the short XyloTrust identity-verification training. Click the button below to start — it takes about 5 minutes.",
        cta: 'Start your training →',
        close: 'Done',
      },
    },
    settings: {
      title: 'Settings',
      subtitle: 'Configuration of your XyloTrust account',
      smsCountries: {
        title: 'Which countries are supported by default?',
        intro: 'XyloTrust supports SMS verification to **19 countries** by default. Belgium is the default choice in the verification picker; the other 18 are one click away.',
        defaultBadge: 'Default',
        supportNote: "A country not listed here?",
        supportNoteRest: 'We activate additional countries on request — contact us at',
        supportNoteEnd: 'with the country codes you need.',
      },
    },
    training: {
      header:    'XyloTrust Training',
      subheader: 'Identity verification',
      steps:     ['Overview', 'Exercise 1', 'Exercise 2', 'Passed'],
      loading:   'Loading...',
      notFound:  'Training link not found or revoked',
      welcome: {
        title:  'Welcome to the XyloTrust training 👋',
        lead:   "In a few minutes you'll learn how to verify the identity of a requester before performing a sensitive operation. Then you'll go through two short exercises. Pass both and you're certified.",
        howItWorks: 'How XyloTrust works',
        steps: [
          { title: '1. Send a verification',  body: 'Open the XyloTrust panel inside the ticket, enter the customer\'s registered mobile number and send the SMS.' },
          { title: '2. The customer verifies', body: 'The customer opens the link, scans their identity document and takes a selfie. The XyloTrust identity verification service compares the two.' },
          { title: '3. Review the result',     body: 'The result appears as a private note. <b>Critical check:</b> does the verified name match the account owner? If not — reject and escalate.' },
        ],
        cta: 'Start exercise 1 →',
      },
      ui: {
        scenarioTag: (n: number) => `Exercise ${n} of 2`,
        evaluate: 'Evaluate this request',
        evaluateLead: 'Read the ticket, send a verification via the XyloTrust panel, and decide based on the result whether to approve or reject the request.',
        ticketCreatedBy: 'Created by Admin User',
        replyTabs: { reply: 'Reply', note: 'Note', forward: 'Forward' },
        replyPlaceholder: 'Type your reply here…',
        widgetTitle: 'Identity check',
        widgetLabel: '1. Customer mobile number',
        widgetSendBtn: '2. Send SMS',
        widgetDirectCheck: 'Direct identity check',
        contactHeader: 'Contact info…',
        contactEdit: 'Edit',
        contactOrg: 'Acme Corp',
        contactEmailLabel: 'Email',
        contactPhoneLabel: 'Mobile phone',
        contactMore: 'Show more',
        progressSent:    'Message sent',
        progressWait:    'Customer is verifying',
        progressWaitSub: 'Waiting on the customer',
        progressDone:    'Verification complete',
        showResults:     'Show results',
        notePrefix:      'Admin User added a private note · just now',
        noteFailedTitle: 'identity verification failed',
        noteFailedBody:  'The XyloTrust identity verification service could not validate the verification.',
        noteOkTitle:     'Identity checks complete.',
        noteDoc:         'Document:',
        noteName:        'Name:',
        noteBirth:       'Place of birth:',
        noteCheck:       'Check:',
        noteCountry:     'Country:',
        noteIP:          'IP address:',
        noteVid:         'Verification ID:',
        decideQ:         'What do you do with this request?',
        decideHint:      'Compare the verified name with the account owner (<b>{account}</b>) and judge the result.',
        approve:         'Approve & proceed',
        reject:          'Reject & escalate',
        feedbackOk:      'Well done',
        feedbackBad:     'Not quite',
        nextBtn:         'Next exercise →',
        finishBtn:       'Finish training →',
        retryBtn:        'Try again',
      },
      passed: {
        title:    "You passed! 🎓",
        body:     "You've completed both exercises correctly and understand when to approve or reject a verification. Your certificate has been sent back to the portal — your admin can now see that you completed the training.",
        certName: 'XyloTrust Identity Verification',
        certMeta: 'completed on',
        already:  'Already completed on',
      },
      scenarios: [
        {
          subject:   'Forgot my password',
          via:       'reported by phone · May 28, 2026 10:56',
          body: [
            'Hi Support,',
            'I forgot my password, can u help me out here?',
            'My email is bill.lates@acmecorp.com',
            'Thanks!',
          ],
          explainOk:  'Correct. The verified name (BILL LATES) matches the account owner and the selfie matches the document. You may proceed with the request.',
          explainBad: "Incorrect. This result is valid: the name matches the account owner and the selfie matches. You should have approved this request.",
        },
        {
          subject:   'Need access to the finance dashboard urgently',
          via:       'reported by email · Jun 3, 2026 09:41',
          body: [
            'Hi,',
            'I urgently need my access restored to the finance dashboard, I have a board meeting in 5 minutes. Please just push it through.',
            'Regards',
          ],
          explainOk:  'Incorrect. The verification failed ("identity verification failed"). A failed verification means you must never execute a sensitive request — you should have rejected and escalated.',
          explainBad: 'Correct. The verification failed, so the identity has not been confirmed. You reject the request and escalate per the SOP — especially given the unusual urgency here (a classic social-engineering signal).',
        },
      ],
    },
    auth: { loginFailed: 'Login failed', retry: 'Try again', loggingIn: 'Signing in...' },
    uninstallBanner: {
      title: 'XyloTrust has been uninstalled',
      bodyDays: 'Your data will be deleted within {days} days in accordance with our terms.',
      bodyToday: 'Your data will be deleted today in accordance with our terms.',
      cta: 'Reinstall the app to prevent this.',
      reinstallLink: 'Installation instructions',
    },
  },
} as const;

export type Translations = typeof T[Lang];

// ── Context ───────────────────────────────────────────────────────────────────
interface LangCtx { lang: Lang; setLang: (l: Lang) => void; t: Translations; }
const Ctx = createContext<LangCtx>({ lang: 'nl', setLang: () => {}, t: T.nl });

function getInitialLang(): Lang {
  return localStorage.getItem('portal_lang') === 'en' ? 'en' : 'nl';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);
  const setLang = (l: Lang) => { localStorage.setItem('portal_lang', l); setLangState(l); };
  return <Ctx.Provider value={{ lang, setLang, t: T[lang] }}>{children}</Ctx.Provider>;
}

export function useLang() { return useContext(Ctx); }
